/** Reserva como devuelve GET /api/console/calendario/bookings */
export type PwaCalendarBooking = {
  id: string;
  resourceId: string;
  resourceName: string;
  resourceColor?: string | null;
  agentId: string;
  agentName: string;
  customerName: string;
  startDate: string;
  endDate: string;
  status: string;
  createdBy?: string;
  publicReference?: string | null;
  conversationId?: string | null;
  sourceMessageId?: string | null;
  sourceMessagePreview?: string | null;
  externalId?: string | null;
  externalSource?: string | null;
  bookingOrigin?: 'internal' | 'ota';
  otaChannel?: 'airbnb' | 'booking' | 'vrbo' | 'expedia' | 'other';
  conversationChannel?: string;
  customerEmail?: string | null;
  customerPhone?: string | null;
  partySize?: number | null;
  quotedTotalCents?: number | null;
  quotedCurrency?: string | null;
  timeBlock?: string | null;
  resourceTypeName?: string | null;
  checkInTime?: string | null;
  checkOutTime?: string | null;
  /** Comparte fechas con un bloqueo importado de Airbnb. */
  alsoBlockedOnAirbnb?: boolean;
};

export type DayInfo = {
  day: number;
  isCurrentMonth: boolean;
  date: Date;
};

const SPANISH_MONTH_NAMES = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
];

export function getSpanishMonthName(month: number): string {
  return SPANISH_MONTH_NAMES[month - 1] ?? '';
}

export function formatMonthHeader(year: number, month: number): string {
  return `${getSpanishMonthName(month)} ${year}`;
}

export function navigateMonth(
  year: number,
  month: number,
  direction: 'prev' | 'next'
): { year: number; month: number } {
  if (direction === 'prev') {
    if (month === 1) return { year: year - 1, month: 12 };
    return { year, month: month - 1 };
  }
  if (month === 12) return { year: year + 1, month: 1 };
  return { year, month: month + 1 };
}

export function isSameCalendarDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** YYYY-MM-DD para la ruta /calendar/reservas/:date */
export function dateKeyFromDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function formatSelectedDayLabel(date: Date): string {
  return date.toLocaleDateString('es', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export function getMonthGrid(year: number, month: number): DayInfo[][] {
  const firstOfMonth = new Date(year, month - 1, 1);
  const lastOfMonth = new Date(year, month, 0);
  const daysInMonth = lastOfMonth.getDate();
  const firstDayOfWeek = (firstOfMonth.getDay() + 6) % 7;

  const weeks: DayInfo[][] = [];
  let currentWeek: DayInfo[] = [];

  if (firstDayOfWeek > 0) {
    const prevMonthLastDay = new Date(year, month - 1, 0).getDate();
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      const day = prevMonthLastDay - i;
      currentWeek.push({
        day,
        isCurrentMonth: false,
        date: new Date(year, month - 2, day),
      });
    }
  }

  for (let day = 1; day <= daysInMonth; day++) {
    currentWeek.push({
      day,
      isCurrentMonth: true,
      date: new Date(year, month - 1, day),
    });

    if (currentWeek.length === 7) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
  }

  if (currentWeek.length > 0) {
    let nextDay = 1;
    while (currentWeek.length < 7) {
      currentWeek.push({
        day: nextDay,
        isCurrentMonth: false,
        date: new Date(year, month, nextDay),
      });
      nextDay++;
    }
    weeks.push(currentWeek);
  }

  return weeks;
}

/** Día civil UTC. Las reservas de Airbnb/OTA se guardan así. */
function utcDayWindow(date: Date): { start: number; end: number } {
  const start = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
  return { start, end: start + 86_400_000 };
}

/** Día local de la ficha. En Uruguay, la medianoche UTC se ve a las 21:00 del día anterior. */
function localDayWindow(date: Date): { start: number; end: number } {
  const start = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  return { start, end: start + 86_400_000 };
}

function isSystemBooking(booking: {
  bookingOrigin?: PwaCalendarBooking['bookingOrigin'];
  createdBy?: string;
}): boolean {
  if (booking.bookingOrigin === 'ota') return false;
  if (booking.bookingOrigin === 'internal') return true;
  return booking.createdBy === 'visitor' || booking.createdBy === 'operator';
}

/** Día de check-in o check-out, con la misma regla de calendario que `getBookingsForDay`. */
export function bookingBoundaryDateKey(
  booking: Pick<PwaCalendarBooking, 'bookingOrigin' | 'createdBy' | 'startDate' | 'endDate'>,
  which: 'start' | 'end',
): string | null {
  const raw = which === 'start' ? booking.startDate : booking.endDate;
  const date = new Date(raw);
  if (!Number.isFinite(date.getTime())) return null;
  if (isSystemBooking(booking)) return dateKeyFromDate(date);
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Interactive Talker usa el día local; Airbnb/OTA usa el día UTC de la importación. */
export function getBookingsForDay(bookings: PwaCalendarBooking[], date: Date): PwaCalendarBooking[] {
  return bookings.filter((booking) => {
    const start = new Date(booking.startDate).getTime();
    const end = new Date(booking.endDate).getTime();
    if (!Number.isFinite(start) || !Number.isFinite(end)) return false;
    const day = isSystemBooking(booking) ? localDayWindow(date) : utcDayWindow(date);
    return start < day.end && end > day.start;
  });
}

/** Reserva creada en Interactive Talker (operador o visitante), no importada de una OTA. */
export function isInternalCalendarBooking(booking: {
  bookingOrigin?: PwaCalendarBooking['bookingOrigin'];
  createdBy?: string;
}): boolean {
  if (booking.bookingOrigin === 'ota') return false;
  if (booking.bookingOrigin === 'internal') return true;
  return booking.createdBy !== 'ical_import';
}

/**
 * En un mismo día y recurso, la reserva interna tapa a la de Airbnb/OTA.
 * Las internas quedan primero para no caer en el recorte de la celda.
 */
export function preferInternalBookings<
  T extends {
    resourceId: string;
    startDate: string;
    bookingOrigin?: PwaCalendarBooking['bookingOrigin'];
    createdBy?: string;
  },
>(bookings: T[]): T[] {
  const internalResources = new Set(
    bookings.filter((booking) => isInternalCalendarBooking(booking)).map((booking) => booking.resourceId)
  );
  return bookings
    .filter((booking) => isInternalCalendarBooking(booking) || !internalResources.has(booking.resourceId))
    .sort((a, b) => {
      const rank = (booking: T) => (isInternalCalendarBooking(booking) ? 0 : 1);
      const byOrigin = rank(a) - rank(b);
      if (byOrigin !== 0) return byOrigin;
      return new Date(a.startDate).getTime() - new Date(b.startDate).getTime();
    });
}

export type CalendarResourceRef = {
  id: string;
  name: string;
  color?: string | null;
  agentName?: string;
};

/** Recursos sin reserva confirmada que cubra el día (endDate exclusivo). */
export function getFreeResourcesForDay<T extends CalendarResourceRef>(
  resources: T[],
  bookings: PwaCalendarBooking[],
  date: Date
): T[] {
  const bookedIds = new Set(getBookingsForDay(bookings, date).map((b) => b.resourceId));
  return resources.filter((r) => !bookedIds.has(r.id));
}

export function sortBookingsByStart(a: PwaCalendarBooking, b: PwaCalendarBooking): number {
  return new Date(a.startDate).getTime() - new Date(b.startDate).getTime();
}

/** Sentinel del filtro «Solo libres» en el combo de calendario. */
export const CAL_FILTER_FREE = '__free__';
